import React, {useCallback, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import loadable from '@loadable/component'
import {FormattedMessage, useIntl} from 'react-intl'
import {HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {
    AspectRatio,
    Box,
    Button,
    Flex,
    IconButton,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image'
import Badges from '../../product-badges'
import {AddIcon, ChevronLeftIcon, ChevronRightIcon, Mute, Pause, Play, Unmute} from '../../icons'
import Video from '../../video'
import {renderComponentsSwitch} from '../../optimizely/utils/contentRenderer'
import {sendClickEvent} from '../../../utils/gtm-utils'
import QVTrigger from './qv-trigger'

// Max number of alternative images to show in the carousel slider (positions 1–5)
const MAX_CAROUSEL_IMAGES = 5

// Lazy load QuickView - only loaded when user clicks to quick view a product
// This defers Chakra Modal/Drawer components and product-view until needed
const QuickView = loadable(() => import('../../../components/quick-view'), {ssr: false})

/**
 * Inline video player for product tile carousel slots.
 * Plays only when the tile is in the viewport AND this slide is the active one.
 * Uses preload="none" and IntersectionObserver to prevent background playback.
 */
const VideoTileItem = ({src, alt, isActive, isTileVisible, wrapperStyle, elementStyle}) => {
    const ref = useRef(null)
    const shouldPlay = isActive && isTileVisible
    // Ref mirrors current shouldPlay so the onCanPlay handler avoids stale closures
    const shouldPlayRef = useRef(shouldPlay)
    useEffect(() => {
        shouldPlayRef.current = shouldPlay
    }, [shouldPlay])

    const [isMuted, setIsMuted] = useState(true)
    const [isPlaying, setIsPlaying] = useState(true)

    // The source is attached only once this slot first becomes playable, so tiles below
    // the fold — and inactive carousel slides — issue no media request on page load.
    // `preload="none"` alone is not enough: an `autoplay` video is fetched regardless of
    // the preload hint, which is why `autoPlay` is bound to `shouldPlay` below.
    const [hasLoaded, setHasLoaded] = useState(false)
    useEffect(() => {
        if (shouldPlay) setHasLoaded(true)
    }, [shouldPlay])

    useEffect(() => {
        const video = ref.current
        if (!video || !hasLoaded) return
        // Set both the DOM property AND the HTML attribute:
        // - DOM property (video.muted) is checked by Chrome/Firefox at play() time
        // - HTML attribute (setAttribute) is checked by Safari for autoplay policy
        // Both must be set because React's reconciliation may reset the DOM property
        // between our imperative set and when the browser evaluates the async play() call.
        video.muted = isMuted
        if (isMuted) {
            video.setAttribute('muted', '')
        } else {
            video.removeAttribute('muted')
        }
        if (shouldPlay) {
            video.play().catch(() => {})
        } else {
            video.pause()
        }
    }, [shouldPlay, isMuted, hasLoaded])

    // Retry play() once the browser signals it has enough data to start.
    // Guards against the race condition where play() is called before buffering begins.
    const handleCanPlay = useCallback(() => {
        const video = ref.current
        if (!video || !shouldPlayRef.current || !video.paused) return
        video.play().catch(() => {})
    }, [])

    const togglePlayPause = useCallback((e) => {
        e.preventDefault()
        e.stopPropagation()
        const video = ref.current
        if (!video) return
        if (video.paused) {
            video.play().catch(() => {})
        } else {
            video.pause()
        }
    }, [])

    const toggleMute = useCallback(
        (e) => {
            e.preventDefault()
            e.stopPropagation()
            const nextMuted = !isMuted
            const video = ref.current
            if (video) {
                video.muted = nextMuted
                if (nextMuted) video.setAttribute('muted', '')
                else video.removeAttribute('muted')
            }
            setIsMuted(nextMuted)
        },
        [isMuted]
    )

    const [showControls, setShowControls] = useState(false)

    return (
        <Box
            {...wrapperStyle}
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
            onClick={(e) => {
                e.stopPropagation()
                setShowControls((prev) => !prev)
            }}
        >
            {/* Use a native <video> element — Chakra's <Box as="video"> can silently drop
                src/muted/loop/playsInline when those props conflict with its style system.
                autoPlay + muted is the browser-native muted-autoplay path (always allowed
                by browser autoplay policy) and matches the existing working Video component. */}
            <video
                ref={ref}
                src={hasLoaded ? src : undefined}
                autoPlay={shouldPlay}
                muted={isMuted}
                loop
                playsInline
                preload="none"
                aria-label={alt}
                onCanPlay={handleCanPlay}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                style={elementStyle}
            />
            {showControls && (
                <Box
                    position="absolute"
                    top={{base: '32px', lg: '34px'}}
                    right={{base: '8px', lg: '6px'}}
                    display="flex"
                    flexDirection="column"
                    gap="12px"
                    zIndex={2}
                >
                    <IconButton
                        aria-label={isPlaying ? 'Pause' : 'Play'}
                        onClick={togglePlayPause}
                        variant="unstyled"
                        boxSize="24px"
                        minW="24px"
                        h="24px"
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        color="white"
                        _hover={{bg: 'transparent'}}
                        _active={{bg: 'transparent'}}
                        _focus={{boxShadow: 'none'}}
                        icon={isPlaying ? <Pause /> : <Play />}
                    />
                    <IconButton
                        aria-label={isMuted ? 'Unmute' : 'Mute'}
                        onClick={toggleMute}
                        variant="unstyled"
                        boxSize="24px"
                        minW="24px"
                        h="24px"
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        color="white"
                        _hover={{bg: 'transparent'}}
                        _active={{bg: 'transparent'}}
                        _focus={{boxShadow: 'none'}}
                        icon={isMuted ? <Unmute /> : <Mute />}
                    />
                </Box>
            )}
        </Box>
    )
}

const ProductTileMedia = (props) => {
    const intl = useIntl()
    const {
        styles,
        productObject,
        hasTileVideo,
        tileVideoSrc,
        videoProps,
        responsiveMediaBlock,
        isLayout,
        showHoverImage,
        enableImageCarousel,
        isDesktop,
        mainImages,
        mainImage: mainImageProp,
        mediaItems,
        hoveredImage,
        dynamicImageProps,
        shouldSkipQuickView,
        showQV,
        isGiftCard,
        isHydrated,
        isQuickViewOpen,
        handleNotifyMeModalState,
        setIsQuickViewOpen,
        productId,
        setColorValue,
        setProductId,
        onQuickViewCloseHandler,
        isWishlistPage,
        wishlistProductId,
        onWishlistToggle,
        hasLengthVariant,
        product,
        setSelectedVariantId,
        setQvProduct,
        showQuickView,
        tileMode,
        handleCartItem,
        isHovered,
        handleShopSimilarModalState,
        responsiveDisplay,
        onLocalSelectionsChange
    } = props
    const [carouselIndex, setCarouselIndex] = useState(0)
    // Prefer the explicit main image (small grid uses the Nth image); fall back to the first.
    const mainImage = mainImageProp || mainImages?.[0] || ''
    // Use mediaItems (mixed image+video) when available, otherwise fall back to plain image URLs
    const activeMediaItems = mediaItems || null
    // Cap carousel images to MAX_CAROUSEL_IMAGES to avoid loading too many images per tile
    const carouselImages = (mainImages?.length > 0 ? mainImages : [mainImage]).slice(
        0,
        MAX_CAROUSEL_IMAGES
    )
    // When mediaItems are present, cap them the same way
    const carouselMedia = activeMediaItems
        ? activeMediaItems.slice(0, MAX_CAROUSEL_IMAGES)
        : null
    const carouselCount = carouselMedia ? carouselMedia.length : carouselImages.length
    // A video alone must not force carousel mode — only the feature flag does.
    const hasCarousel = isDesktop && enableImageCarousel && hoveredImage
    // Hover mode: slot 0 = main (video here disables hover), slot 1 = hover, slot 2+ = hidden.
    const mainMediaItem = carouselMedia?.[0]
    const hoverMediaItem = carouselMedia?.[1]
    const mainIsVideo = mainMediaItem?.type === 'video'
    const hoverIsVideo = hoverMediaItem?.type === 'video'
    // On mobile with carousel enabled, render all images in the native horizontal swipe row
    // instead of the 2-image flex row, so the user can swipe through all available images.
    const mobileSwipeSource = carouselMedia ||
        (carouselImages.length > 1
            ? [mainImage, ...carouselImages.filter((image) => image !== mainImage)]
            : null)
    const mobileSwipeImages = !isDesktop && mobileSwipeSource ? mobileSwipeSource : null
    const isDesktopQuickView = isDesktop && tileMode === 'quick-view'
    const isQuickAdd = tileMode === 'quick-add'

    const trackAddToBagIconClick = () => {
        const isPdpPage =
            typeof window !== 'undefined' &&
            /\/(product|([a-zA-Z]{2}\/)?p\/)/.test(window.location.pathname)
        const trackedProduct = productObject || product

        if (isPdpPage && trackedProduct) {
            sendClickEvent('add_to_bag_icon', trackedProduct)
        } else {
            sendClickEvent('quick_add_clicked')
        }
    }

    // --- Lazy loading strategy ---
    // Behavior A (hover): the hover image is NOT rendered until the user first hovers the tile.
    //   Once triggered, hasBeenHovered stays true so the image remains cached in the DOM.
    // Behavior B (slider): an IntersectionObserver defers loading of carousel images (positions 2–5)
    //   until the tile enters the viewport, combined with native loading="lazy" as a fallback.
    //   This avoids eagerly loading up to 5 images per tile across the entire PLP grid.
    const [hasBeenHovered, setHasBeenHovered] = useState(false)
    const [isTileVisible, setIsTileVisible] = useState(false)
    const observerRef = useRef(null)
    // Desktop carousel: tracks which indices are unlocked for loading.
    // Initialised with {0, 1} so the first navigation never has a blank frame.
    const [loadedIndices, setLoadedIndices] = useState(() => new Set([0, 1]))
    // Mobile swipe: highest index that has been unlocked for loading.
    // Initialised to 1 so the first swipe never has a blank frame.
    const [mobileMaxLoaded, setMobileMaxLoaded] = useState(1)

    // Callback ref: attaches IntersectionObserver for carousel lazy loading (Behavior B).
    // Callback refs fire during commit, guaranteeing the node is available.
    const wrapperCallbackRef = useCallback((node) => {
        // Clean up previous observer if the node changes
        if (observerRef.current) {
            observerRef.current.disconnect()
            observerRef.current = null
        }
        if (!node) return
        if (typeof IntersectionObserver === 'undefined') {
            setIsTileVisible(true)
            return
        }
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsTileVisible(true)
                    observer.disconnect()
                    observerRef.current = null
                }
            },
            {rootMargin: '200px'}
        )
        observer.observe(node)
        observerRef.current = observer
    }, [])

    // Disconnect observer on unmount
    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect()
            }
        }
    }, [])

    // Track first hover for lazy hover-image loading (Behavior A)
    useEffect(() => {
        if (isHovered && !hasBeenHovered) {
            setHasBeenHovered(true)
        }
    }, [isHovered, hasBeenHovered])

    useEffect(() => {
        if (!hasCarousel) {
            setCarouselIndex(0)
        }
    }, [hasCarousel])

    // When the desktop carousel navigates, unlock the adjacent indices so they
    // preload in the background — by the time the user reaches them, no flicker.
    useEffect(() => {
        if (!hasCarousel) return
        setLoadedIndices((prev) => {
            const toAdd = [carouselIndex - 1, carouselIndex, carouselIndex + 1].filter(
                (i) => i >= 0 && i < carouselCount
            )
            if (toAdd.every((i) => prev.has(i))) return prev
            const next = new Set(prev)
            toAdd.forEach((i) => next.add(i))
            return next
        })
    }, [carouselIndex, carouselCount, hasCarousel])

    // Mobile swipe: unlock the next image as the user scrolls so it preloads
    // before they reach it. Uses the scroll container's own geometry.
    const handleMobileScroll = useCallback((e) => {
        const el = e.currentTarget
        if (!el.clientWidth) return
        const visibleIndex = Math.round(el.scrollLeft / el.clientWidth)
        setMobileMaxLoaded((prev) => Math.max(prev, visibleIndex + 1))
    }, [])

    const videoRef = useRef(null)
    const [isVideoPlaying, setIsVideoPlaying] = useState(true)

    const handleToggleVideo = (event) => {
        event.preventDefault()
        event.stopPropagation()
        const video = videoRef.current
        if (!video) return
        if (video.paused) {
            video.play()
            setIsVideoPlaying(true)
        } else {
            video.pause()
            setIsVideoPlaying(false)
        }
    }

    // Keyboard handler for carousel arrows — supports Enter and Space.
    // Declared before any early return so hook order stays stable across the
    // video / responsive-media / carousel render paths.
    const handleCarouselKeyDown = useCallback(
        (e, direction) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                if (direction === 'prev') {
                    setCarouselIndex((current) => (current === 0 ? carouselCount - 1 : current - 1))
                } else {
                    setCarouselIndex((current) => (current === carouselCount - 1 ? 0 : current + 1))
                }
            }
        },
        [carouselCount]
    )

    const renderQuickAddOverlay = () => {
        if (!showQV || isGiftCard || !isHydrated) {
            return null
        }

        if (!shouldSkipQuickView) {
            return (
                <Flex {...styles.quickViewOverlay} sx={responsiveDisplay?.quickActionsDisplay}>
                    {isQuickViewOpen && !isDesktopQuickView ? (
                        <QuickView
                            isNotifyMeModalStateOpen={handleNotifyMeModalState}
                            setIsQuickViewOpen={setIsQuickViewOpen}
                            productId={productId}
                            setColorValue={setColorValue}
                            setProductId={setProductId}
                            onQuickViewClose={onQuickViewCloseHandler}
                            isWishlistPage={isWishlistPage}
                            wishlistProductId={wishlistProductId}
                            onWishlistToggle={onWishlistToggle}
                            hasLengthVariant={hasLengthVariant}
                            searchHitData={product}
                            onVariantSelect={setSelectedVariantId}
                            onProductChange={setQvProduct}
                            onLocalSelectionsChange={onLocalSelectionsChange}
                            enableAutoScroll={false}
                            isQuickAdd={isQuickAdd}
                            onShopSimilarStateChange={handleShopSimilarModalState}
                            themeVariant={tileMode}
                        />
                    ) : (
                        <Button
                            onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                showQuickView(e.currentTarget)
                                // GTM Modification
                                trackAddToBagIconClick()
                            }}
                            aria-label={intl.formatMessage(
                                {
                                    defaultMessage: 'Add {productName} to cart',
                                    id: 'product_tile.button.quick_add_aria_label'
                                },
                                {
                                    productName:
                                        productObject?.productName || productObject?.name || 'product'
                                }
                            )}
                            variant="quickViewBtn"
                            {...styles.quickViewButton}
                            sx={{
                                '&:hover [data-qv-trigger] svg': {
                                    transform: 'rotate(90deg)'
                                }
                            }}
                            data-testid="quick-add"
                        >
                            {tileMode !== 'quick-view' && (
                                <HideOnMobile>
                                    <Text as="span" {...styles.innerTextButton}>
                                        <FormattedMessage
                                            defaultMessage="Quick Add"
                                            id="product_tile.button.quick_add"
                                        />
                                    </Text>
                                </HideOnMobile>
                            )}
                            <QVTrigger styles={styles.quickViewIcon} />
                        </Button>
                    )}
                </Flex>
            )
        }

        return (
            <Flex {...styles.quickViewOverlay} sx={responsiveDisplay?.quickActionsDisplay}>
                <Button
                    onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        // GTM Modification
                        trackAddToBagIconClick()
                        handleCartItem()
                    }}
                    variant="quickViewBtn"
                    {...styles.quickViewButton}
                    sx={{
                        '&:hover [data-qv-trigger] svg': {
                            transform: 'rotate(90deg)'
                        }
                    }}
                >
                    <HideOnMobile>
                        <Text as="span" {...styles.innerTextButton}>
                            <FormattedMessage
                                defaultMessage="Add to bag"
                                id="product_tile.button.add_to_bag"
                            />
                        </Text>
                    </HideOnMobile>
                    <QVTrigger styles={styles.quickViewIcon} />
                </Button>
            </Flex>
        )
    }

    if (hasTileVideo) {
        return (
            <Box {...styles.imageWrapper} {...(responsiveDisplay?.imageWrapperOverride || {})}>
                <Box sx={responsiveDisplay?.onImageBadge}>
                    <Badges badges={productObject?.c_badges} />
                </Box>
                <AspectRatio {...styles.mediaAspect}>
                    <Video
                        src={tileVideoSrc || productObject?.representedProduct?.c_mediaProductTile}
                        preload={videoProps?.preload ? 'metadata' : 'auto'}
                        ref={videoRef}
                    />
                </AspectRatio>
                <IconButton
                    aria-label={isVideoPlaying ? 'Pause video' : 'Play video'}
                    icon={isVideoPlaying ? <Pause /> : <Play />}
                    onClick={handleToggleVideo}
                    {...styles.videoToggleButton}
                />
                {renderQuickAddOverlay()}
            </Box>
        )
    }

    if (responsiveMediaBlock) {
        return (
            <Box {...styles.imageWrapper} {...(responsiveDisplay?.imageWrapperOverride || {})}>
                <Box {...styles.mediaScroller}>
                    {isLayout ? (
                        <Box {...styles.responsiveMediaBlock}>
                            {renderComponentsSwitch(responsiveMediaBlock)}
                        </Box>
                    ) : (
                        <AspectRatio {...styles.mediaAspect} {...styles.responsiveMediaBlock}>
                            {renderComponentsSwitch(responsiveMediaBlock)}
                        </AspectRatio>
                    )}
                </Box>
                {renderQuickAddOverlay()}
            </Box>
        )
    }


    return (
        <Box
            ref={wrapperCallbackRef}
            {...styles.imageWrapper}
            {...(isGiftCard && {bg: 'webTheme.backgroundPrimaryBrand'})}
            {...(responsiveDisplay?.imageWrapperOverride || {})}
        >
            <Box position={'absolute'}
                top={{lg: 1}}
                bottom={{base: 1, lg: 'auto'}}
                left={1}
                zIndex={3}>
                <Badges badges={productObject?.c_badges} />
            </Box>
            <Box
                {...styles.mediaScroller}
                data-pt-media-scroller="true"
                onScroll={mobileSwipeImages ? handleMobileScroll : undefined}
            >
                {hasCarousel ? (
                    <>
                        <Box
                            {...styles.mediaCarouselTrack}
                            data-pt-media-row="true"
                            style={{
                                width: `${carouselCount * 100}%`,
                                transform: `translateX(-${carouselIndex * (100 / carouselCount)}%)`
                            }}
                        >
                            {(carouselMedia || carouselImages).map((item, idx) => {
                                const isVideoItem = item?.type === 'video'
                                const imgSrc = isVideoItem ? null : (item?.src || item)
                                // Defer loading non-primary carousel images until the tile
                                // enters the viewport (IntersectionObserver). Use empty string
                                // instead of undefined to avoid DynamicImage calling .replace()
                                // on an undefined src.
                                // idx 0 always loads; idx 1 loads when tile is visible (preloaded);
                                // idx 2+ load only after the user has navigated close to them.
                                const deferredSrc =
                                    idx === 0
                                        ? imgSrc
                                        : isTileVisible && loadedIndices.has(idx)
                                        ? imgSrc
                                        : ''
                                return (
                                    <Box
                                        key={idx}
                                        {...styles.mediaCarouselSlide}
                                        style={{flex: `0 0 ${100 / carouselCount}%`}}
                                    >
                                        {isVideoItem ? (
                                            <VideoTileItem
                                                src={item.src}
                                                alt={item.alt}
                                                isActive={carouselIndex === idx}
                                                isTileVisible={isTileVisible}
                                                wrapperStyle={styles.videoTileWrapper}
                                                elementStyle={styles.videoTileElement}
                                            />
                                        ) : (
                                            <AspectRatio {...styles.mediaAspect}>
                                                <DynamicImage
                                                    data-testid={
                                                        idx === 0
                                                            ? 'product-tile-image'
                                                            : 'product-tile-hover-image'
                                                    }
                                                    src={deferredSrc}
                                                    widths={dynamicImageProps?.widths}
                                                    imageProps={{
                                                        alt:
                                                            productObject?.productName ||
                                                            productObject?.name,
                                                        loading:
                                                            idx === 0
                                                                ? dynamicImageProps?.imageProps
                                                                      ?.loading ?? 'lazy'
                                                                : 'lazy',
                                                        ...(idx === 0
                                                            ? dynamicImageProps?.imageProps
                                                            : {
                                                                  decoding: 'async',
                                                                  fetchpriority: 'low'
                                                              }),
                                                        width: '100%'
                                                    }}
                                                />
                                            </AspectRatio>
                                        )}
                                    </Box>
                                )
                            })}
                        </Box>
                        {/* Arrows visible on hover and always via keyboard focus-within */}
                        <Flex
                            {...styles.mediaCarouselNav}
                            sx={{
                                visibility: isHovered ? 'visible' : 'hidden',
                                '&:focus-within': {visibility: 'visible'}
                            }}
                        >
                            <IconButton
                                {...styles.mediaCarouselButton}
                                aria-label="Previous image"
                                icon={<ChevronLeftIcon {...styles.mediaCarouselIcon} />}
                                data-testid="product-tile-button-prev-image"
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    setCarouselIndex((current) =>
                                        current === 0 ? carouselCount - 1 : current - 1
                                    )
                                }}
                                onKeyDown={(e) => handleCarouselKeyDown(e, 'prev')}
                            />
                            <IconButton
                                {...styles.mediaCarouselButton}
                                aria-label="Next image"
                                icon={<ChevronRightIcon {...styles.mediaCarouselIcon} />}
                                data-testid="product-tile-button-next-image"
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    setCarouselIndex((current) =>
                                        current === carouselCount - 1 ? 0 : current + 1
                                    )
                                }}
                                onKeyDown={(e) => handleCarouselKeyDown(e, 'next')}
                            />
                        </Flex>
                    </>
                ) : mobileSwipeImages ? (
                    // Mobile with carousel enabled: swipe row with all available images.
                    // Each image fills 100% of the visible tile width; overflow-x:auto on
                    // mediaScroller provides the native touch swipe — no arrow UI needed.
                    <Box
                        data-pt-media-row="true"
                        style={{width: `${mobileSwipeImages.length * 100}%`, display: 'flex'}}
                    >
                        {mobileSwipeImages.map((item, idx) => {
                            const isVideoItem = item?.type === 'video'
                            const imgSrc = isVideoItem ? null : (item?.src || item)
                            return (
                                <Box
                                    key={idx}
                                    style={{
                                        flex: `0 0 ${100 / mobileSwipeImages.length}%`,
                                        width: `${100 / mobileSwipeImages.length}%`
                                    }}
                                >
                                    {isVideoItem ? (
                                        <VideoTileItem
                                            src={item.src}
                                            alt={item.alt}
                                            isActive={idx <= mobileMaxLoaded}
                                            isTileVisible={isTileVisible}
                                            wrapperStyle={styles.videoTileWrapper}
                                            elementStyle={styles.videoTileElement}
                                        />
                                    ) : (
                                        <AspectRatio {...styles.mediaAspect}>
                                            <DynamicImage
                                                data-testid={idx === 0 ? 'product-tile-image' : 'product-tile-hover-image'}
                                                src={
                                                    idx === 0
                                                        ? imgSrc
                                                        : isTileVisible && idx <= mobileMaxLoaded
                                                        ? imgSrc
                                                        : ''
                                                }
                                                widths={dynamicImageProps?.widths}
                                                imageProps={{
                                                    alt: productObject?.productName || productObject?.name,
                                                    loading: idx === 0
                                                        ? dynamicImageProps?.imageProps?.loading ?? 'lazy'
                                                        : 'lazy',
                                                    ...(idx === 0 ? dynamicImageProps?.imageProps : {
                                                        decoding: 'async',
                                                        fetchpriority: 'low'
                                                    }),
                                                    width: '100%'
                                                }}
                                            />
                                        </AspectRatio>
                                    )}
                                </Box>
                            )
                        })}
                    </Box>
                ) : (
                    // Default: main + optional hover. Video by slot: 0 = main, 1 = hover.
                    <Box {...styles.mediaImageRow} data-pt-media-row="true">
                        {mainIsVideo ? (
                            <AspectRatio {...styles.mediaAspect}>
                                <VideoTileItem
                                    src={mainMediaItem.src}
                                    alt={productObject?.productName || productObject?.name}
                                    isActive={true}
                                    isTileVisible={isTileVisible}
                                    wrapperStyle={styles.videoTileWrapper}
                                    elementStyle={styles.videoTileElement}
                                />
                            </AspectRatio>
                        ) : (
                            <AspectRatio {...styles.mediaAspect}>
                                <DynamicImage
                                    data-testid="product-tile-image"
                                    src={mainImage}
                                    widths={dynamicImageProps?.widths}
                                    imageProps={{
                                        alt: productObject?.productName || productObject?.name,
                                        loading: dynamicImageProps?.imageProps?.loading ?? 'lazy',
                                        ...dynamicImageProps?.imageProps,
                                        width: isGiftCard ? '75%' : '100%',
                                        borderRadius: isGiftCard ? '8px' : '0'
                                    }}
                                />
                            </AspectRatio>
                        )}
                        {/* No hover slot when the main is a video. */}
                        {!mainIsVideo && hoverIsVideo && (!isDesktop || hasBeenHovered) && (
                            <AspectRatio {...styles.mediaAspect} {...styles.mediaHoverAspect}>
                                <VideoTileItem
                                    src={hoverMediaItem.src}
                                    alt={productObject?.productName || productObject?.name}
                                    isActive={isHovered}
                                    isTileVisible={isTileVisible}
                                    wrapperStyle={styles.videoTileWrapper}
                                    elementStyle={styles.videoTileElement}
                                />
                            </AspectRatio>
                        )}
                        {/* Hover image lazy load:
                            - Desktop: only mount after first mouseenter, then keep cached.
                            - Mobile (carousel disabled): always render — the 200%-wide flex
                              row relies on both images being present for the swipe behavior. */}
                        {!mainIsVideo && !hoverIsVideo && showHoverImage && (!isDesktop || hasBeenHovered) && (
                            <AspectRatio {...styles.mediaAspect} {...styles.mediaHoverAspect}>
                                <DynamicImage
                                    data-testid="product-tile-hover-image"
                                    src={hoveredImage}
                                    widths={dynamicImageProps?.widths}
                                    imageProps={{
                                        alt: productObject?.productName || productObject?.name,
                                        loading: 'lazy',
                                        decoding: 'async',
                                        fetchpriority: 'low',
                                        width: '100%'
                                    }}
                                />
                            </AspectRatio>
                        )}
                    </Box>
                )}
            </Box>
            {renderQuickAddOverlay()}
        </Box>
    )
}

ProductTileMedia.propTypes = {
    styles: PropTypes.object,
    productObject: PropTypes.object,
    showVideo: PropTypes.bool,
    videoProps: PropTypes.shape({
        preload: PropTypes.oneOf(['none', 'metadata', 'auto'])
    }),
    responsiveMediaBlock: PropTypes.object,
    isLayout: PropTypes.bool,
    showHoverImage: PropTypes.bool,
    enableImageCarousel: PropTypes.bool,
    isDesktop: PropTypes.bool,
    hoveredImage: PropTypes.string,
    dynamicImageProps: PropTypes.object,
    shouldSkipQuickView: PropTypes.bool,
    showQV: PropTypes.bool,
    isGiftCard: PropTypes.bool,
    isHydrated: PropTypes.bool,
    isQuickViewOpen: PropTypes.bool,
    handleNotifyMeModalState: PropTypes.func,
    setIsQuickViewOpen: PropTypes.func,
    productId: PropTypes.string,
    setColorValue: PropTypes.func,
    setProductId: PropTypes.func,
    onQuickViewCloseHandler: PropTypes.func,
    isWishlistPage: PropTypes.bool,
    wishlistProductId: PropTypes.string,
    onWishlistToggle: PropTypes.func,
    hasLengthVariant: PropTypes.bool,
    product: PropTypes.object,
    setSelectedVariantId: PropTypes.func,
    setQvProduct: PropTypes.func,
    showQuickView: PropTypes.func,
    tileMode: PropTypes.string,
    handleCartItem: PropTypes.func,
    isHovered: PropTypes.bool,
    mainImages: PropTypes.arrayOf(PropTypes.string),
    mainImage: PropTypes.string,
    mediaItems: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
    handleShopSimilarModalState: PropTypes.func,
    responsiveDisplay: PropTypes.object
}

export default ProductTileMedia
